import React, { useState } from 'react';
import { getDoctorByEmail, getPatientsByDoctor } from '../api/database';
import { authenticatedFetch } from '../api/authenticatedFetch';
import { apiUrl } from '../config/appConfig';

type Page = 'login' | 'register' | 'dashboard' | 'history' | 'timeline' | 'settings' | 'consent' | 'thresholds' | 'patients' | 'medication';

export type UserProfile = { name: string; surname?: string; email: string; language: string; passwordLength?: number };

type SettingsPageProps = {
  onLogout: () => void;
  onNavigate: (page: Page) => void;
  onPatIdChange?: (patId: string) => void;
  user: UserProfile | null;
  onUpdateUser: (updates: Partial<UserProfile>) => void;
  userRole?: string;
  isDemo?: boolean;
  /** When set, doctor Thresholds row navigates to preset thresholds (no patient picker). */
  onOpenPresetThresholds?: () => void;
};

const SettingsPage: React.FC<SettingsPageProps> = ({ onLogout, onNavigate, onPatIdChange, user, onUpdateUser, userRole, isDemo, onOpenPresetThresholds }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [firstName, setFirstName] = useState(user?.name || '');
  const [lastName, setLastName] = useState(user?.surname || '');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPatientSelectionModalOpen, setIsPatientSelectionModalOpen] = useState(false);
  const [patients, setPatients] = useState<Array<{ pat_id: string; first_name: string; last_name: string; dev_id: string }>>([]);
  const [loadingThresholds, setLoadingThresholds] = useState(false);
  const [loadingMedication, setLoadingMedication] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [modalContext, setModalContext] = useState<'thresholds' | 'medication'>('thresholds');

  const openNameModal = () => {
    setFirstName(user?.name || '');
    setLastName(user?.surname || '');
    setIsNameModalOpen(true);
  };

  const saveName = () => {
    onUpdateUser({ name: firstName, surname: lastName });
    setIsNameModalOpen(false);
  };

  const openPasswordModal = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwdError(null);
    setIsPasswordModalOpen(true);
  };

  const openPatientSelectionModal = async () => {
    if (userRole !== 'doctor') return;
    
    setModalContext('thresholds');
    setLoadingThresholds(true);
    try {
      const email = localStorage.getItem('currentUserEmail') || '';
      if (!email) return;
      
      const doc = await getDoctorByEmail(email);
      if (!doc || !doc.doc_id) return;
      
      const pats = await getPatientsByDoctor(doc.doc_id);
      setPatients(pats);
      setIsPatientSelectionModalOpen(true);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingThresholds(false);
    }
  };

  const selectPatientForThresholds = (patId: string) => {
    // Store the selected patient ID and navigate to thresholds
    localStorage.setItem('selectedPatId', patId);
    onPatIdChange?.(patId);
    setIsPatientSelectionModalOpen(false);
    try { window.history.pushState({ page: 'thresholds' }, '', '/thresholds'); } catch {}
    onNavigate('thresholds');
  };

  const openPatientSelectionModalForMedication = async () => {
    if (userRole !== 'doctor') return;
    
    setModalContext('medication');
    setLoadingMedication(true);
    try {
      const email = localStorage.getItem('currentUserEmail') || '';
      if (!email) return;
      
      const doc = await getDoctorByEmail(email);
      if (!doc || !doc.doc_id) return;
      
      const pats = await getPatientsByDoctor(doc.doc_id);
      setPatients(pats);
      setIsPatientSelectionModalOpen(true);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingMedication(false);
    }
  };

  const selectPatientForMedication = (patId: string) => {
    // Store the selected patient ID and navigate to medication
    localStorage.setItem('selectedPatId', patId);
    onPatIdChange?.(patId);
    setIsPatientSelectionModalOpen(false);
    try { window.history.pushState({ page: 'medication' }, '', '/medication'); } catch {}
    onNavigate('medication');
  };

  const savePassword = async () => {
    if (newPassword.length < 6) {
      setPwdError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Passwords do not match.');
      return;
    }
    if (!oldPassword) {
      setPwdError('Please enter your current password.');
      return;
    }

    setPwdLoading(true);
    setPwdError(null);

    try {
      const response = await authenticatedFetch(apiUrl('/users/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user?.email,
          currentPassword: oldPassword,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setPwdError('Current password is incorrect.');
        } else {
          setPwdError(data.error || 'Failed to change password.');
        }
        return;
      }

      // Success - update local state and close modal
      onUpdateUser({ passwordLength: newPassword.length });
      setIsPasswordModalOpen(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwdError(null);
    } catch (error) {
      console.error('Change password error:', error);
      setPwdError('Network error. Check your connection.');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>Settings</h1>
        </div>
        <div />
      </header>

      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">
        <ul className="sidebar-menu">
          <li
            onClick={() => {
              setIsSidebarOpen(false);
              if (userRole === 'doctor' && !isDemo) {
                onNavigate('patients');
              } else {
                onNavigate('dashboard');
              }
            }}
          >
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('history'); }}><span className="mi">🕒</span><span>History</span></li>
          {userRole === 'doctor' && !isDemo && (
            <li onClick={() => { setIsSidebarOpen(false); onNavigate('timeline'); }}><span className="mi">📈</span><span>Timeline</span></li>
          )}
          <li onClick={() => setIsSidebarOpen(false)}><span className="mi">⚙️</span><span>Settings</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('consent'); }}><span className="mi">📝</span><span>Consent</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div className="sidebar-footer">
          <div className="sidebar-brand"></div>
        </div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

      <div className="history-container">
        <div className="settings-card">
          <div className="settings-list">
            <div className="settings-item">
              <div className="si-left"><span className="mi">👤</span><span>{[user?.name, user?.surname].filter(Boolean).join(' ') || '—'}</span></div>
              <button className="si-action" onClick={openNameModal}>Change</button>
            </div>
            <div className="settings-item">
              <div className="si-left"><span className="mi">✉️</span><span>{user?.email || '—'}</span></div>
            </div>
            <div className="settings-item">
              <div className="si-left"><span className="mi">🔑</span><span>{'*'.repeat(Math.max(1, user?.passwordLength ?? 10))}</span></div>
              <button className="si-action" onClick={openPasswordModal}>Change</button>
            </div>
            {/* Language option removed as requested */}
            {userRole === 'doctor' && !isDemo && (
              <div className="settings-item">
                <div className="si-left"><span className="mi">🌡️</span><span>Pre-set thresholds</span></div>
                <button
                  className="si-action"
                  onClick={onOpenPresetThresholds ?? openPatientSelectionModal}
                  disabled={!onOpenPresetThresholds && loadingThresholds}
                >
                  {!onOpenPresetThresholds && loadingThresholds ? 'Loading...' : 'Change'}
                </button>
              </div>
            )}
            {userRole === 'doctor' && !isDemo && (
              <div className="settings-item">
                <div className="si-left"><span className="mi">💊</span><span>Medication</span></div>
                <button
                  className="si-action"
                  onClick={openPatientSelectionModalForMedication}
                  disabled={loadingMedication}
                >
                  {loadingMedication ? 'Loading...' : 'Change'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isNameModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsNameModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="section-title" style={{ borderTop: 'none', paddingTop: 0 }}>Change username</div>
            <div className="input-group">
              <div className="input-content" style={{ width: '100%' }}>
                <label className="input-label">Name</label>
                <input className="input-field" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
            </div>
            <div className="input-group">
              <div className="input-content" style={{ width: '100%' }}>
                <label className="input-label">Surname</label>
                <input className="input-field" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setIsNameModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveName}>Change</button>
            </div>
          </div>
        </div>
      )}

      {isPasswordModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsPasswordModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="section-title" style={{ borderTop: 'none', paddingTop: 0 }}>Change password</div>
            <div className="input-group">
              <div className="input-content" style={{ width: '100%' }}>
                <label className="input-label">Old password</label>
                <input className="input-field" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
              </div>
            </div>
            <div className="input-group">
              <div className="input-content" style={{ width: '100%' }}>
                <label className="input-label">New password</label>
                <input className="input-field" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
            </div>
            <div className="input-group">
              <div className="input-content" style={{ width: '100%' }}>
                <label className="input-label">Confirm new password</label>
                <input className="input-field" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>
            {pwdError && <div className="form-error">{pwdError}</div>}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setIsPasswordModalOpen(false)} disabled={pwdLoading}>Cancel</button>
              <button className="btn-primary" onClick={savePassword} disabled={pwdLoading}>
                {pwdLoading ? 'Changing...' : 'Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Selection Modal for Thresholds */}
      {isPatientSelectionModalOpen && (
        <div 
          className="modal-backdrop" 
          onClick={() => setIsPatientSelectionModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            className="modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'hidden',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            <div 
              className="modal-header"
              style={{
                padding: '24px 24px 16px 24px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.5rem', 
                  fontWeight: '600', 
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ 
                    fontSize: '1.8rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>{modalContext === 'thresholds' ? '🌡️' : '💊'}</span>
                  Select Patient
                </h3>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  color: '#7f8c8d', 
                  fontSize: '0.95rem',
                  fontWeight: '400'
                }}>
                  Choose which patient's {modalContext === 'thresholds' ? 'thresholds' : 'medication'} you want to modify
                </p>
              </div>
              <button 
                className="modal-close" 
                onClick={() => setIsPatientSelectionModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.8rem',
                  color: '#bdc3c7',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.color = '#e74c3c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#bdc3c7';
                }}
              >
                ×
              </button>
            </div>
            
            <div 
              className="modal-body"
              style={{
                padding: '20px 24px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              {patients.length > 0 ? (
                <div className="patient-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {patients.map((patient, index) => (
                    <div
                      key={patient.pat_id}
                      className="patient-item"
                      onClick={() => modalContext === 'thresholds' ? selectPatientForThresholds(patient.pat_id) : selectPatientForMedication(patient.pat_id)}
                      style={{
                        padding: '20px',
                        border: '2px solid #ecf0f1',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#fff',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3498db';
                        e.currentTarget.style.backgroundColor = '#f8f9ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#ecf0f1';
                        e.currentTarget.style.backgroundColor = '#fff';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '16px' 
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {patient.first_name.charAt(0).toUpperCase()}{patient.last_name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '1.1rem',
                            color: '#2c3e50',
                            marginBottom: '4px'
                          }}>
                            {patient.first_name} {patient.last_name}
                          </div>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#7f8c8d',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{
                              backgroundColor: '#ecf0f1',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}>
                              ID: {patient.pat_id}
                            </span>
                          </div>
                        </div>
                        <div style={{
                          color: '#bdc3c7',
                          fontSize: '1.2rem',
                          transition: 'all 0.2s ease'
                        }}>
                          →
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem 2rem',
                  color: '#7f8c8d'
                }}>
                  <div style={{ 
                    fontSize: '3rem', 
                    marginBottom: '1rem',
                    opacity: 0.5
                  }}>
                    👥
                  </div>
                  <h4 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#95a5a6',
                    fontSize: '1.1rem'
                  }}>
                    No patients found
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.9rem',
                    color: '#bdc3c7'
                  }}>
                    This doctor doesn't have any assigned patients yet.
                  </p>
                </div>
              )}
            </div>
            
            <div 
              className="modal-actions"
              style={{
                padding: '16px 24px 24px 24px',
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: '#fafbfc'
              }}
            >
              <button 
                className="btn-secondary" 
                onClick={() => setIsPatientSelectionModalOpen(false)}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;


